import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Accordion, AccordionItem, Card, CardBody } from '@structural-climbing/ui'
import {
  gradesForDiscipline,
  type CompetitionScoringType,
  type Discipline,
} from '@structural-climbing/validation'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'

interface Props {
  /**
   * Which scale's baseScores to show. Defaults to boulder, which is what the
   * gym standings have always shown; a competition passes its own discipline.
   */
  discipline?: Discipline
  /** Link to the personal-progress page. null hides that line entirely. */
  progressLink?: string | null
  /**
   * Which formula to explain. Defaults to `gym_standings`, which is what the
   * gym-wide board always uses and which no competition can pick; a
   * competition passes its own, straight off the standings payload.
   */
  scoringType?: 'gym_standings' | CompetitionScoringType
  /** A combined competition: say how General relates to the two disciplines. */
  combined?: boolean
}

/**
 * "How scoring works" — the rule the board in front of you is actually using.
 *
 * Closed by default. It is reference material you read once, and expanded it
 * cost more vertical space than the standings themselves.
 *
 * Shared by the gym standings and every competition. `gym_standings` gets the
 * per-grade point table plus the flash bonus; `thousand_split` gets prose
 * instead, because there the grade has no bearing on points and showing the
 * table would be an outright lie. `tops_count` is prose for the same reason. Keeping one component means the explanation
 * can never drift away from the formula it describes.
 */
export function ScoringCard({
  discipline = 'boulder',
  progressLink,
  scoringType = 'gym_standings',
  combined = false,
}: Props) {
  const { t } = useTranslation()
  const gradeConfig = useGymGradeConfig()
  const grades = gradesForDiscipline(gradeConfig, discipline)
  const isThousandSplit = scoringType === 'thousand_split'

  return (
    <Card className="shadow-sm">
      <CardBody className="py-1">
        <Accordion isCompact className="px-0">
          <AccordionItem
            key="scoring"
            aria-label={t('standings.scoringTitle')}
            title={<span className="font-semibold">{t('standings.scoringTitle')}</span>}
          >
            {combined && (
              <p className="mb-1.5 text-xs text-foreground/60">{t('standings.scoringCombined')}</p>
            )}
            {scoringType === 'tops_count' ? (
              <div className="mb-1 flex flex-col gap-1.5 text-xs text-foreground/60">
                <p>{t('standings.scoringTopsCount')}</p>
                <p>{t('standings.scoringTopsTies')}</p>
              </div>
            ) : isThousandSplit ? (
              <div className="mb-1 flex flex-col gap-1.5 text-xs text-foreground/60">
                <p>{t('standings.scoringThousandPool')}</p>
                <p>{t('standings.scoringThousandFlash')}</p>
                {/* Said out loud because a score that falls looks like a bug. */}
                <p>{t('standings.scoringThousandMoves')}</p>
              </div>
            ) : (
              <>
                <div className="mb-3 grid grid-cols-4 gap-1 text-sm sm:grid-cols-8">
                  {grades.map((g) => (
                    <div
                      key={g.rank}
                      className="flex flex-col items-center rounded-md bg-content2 px-2 py-1.5"
                    >
                      <span className="truncate text-xs font-bold text-foreground/60">
                        {g.label}
                      </span>
                      <span className="font-bold">{g.baseScore}</span>
                    </div>
                  ))}
                </div>
                <p className="mb-1 text-xs text-foreground/60">
                  {t('standings.scoringFlashBonus')}
                </p>
              </>
            )}
            {progressLink && (
              <p className="pb-1 text-xs text-foreground/50">
                {t('standings.scoringProgressTip')}{' '}
                <Link to={progressLink} className="text-primary underline">
                  {t('progress.menuLink')}
                </Link>
              </p>
            )}
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  )
}
